/**
 * The builder's Downloads panel: make a PDF of this dashboard or report in
 * the background (AgencyAnalytics' Download PDF), and the list of PDFs made
 * so far — Generating, then Completed with Download, or Failed with why.
 * While one is being made the list checks again every few seconds.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { exportsApi, type ReportExport } from './exports-api.ts';
import type { RangeSetting } from './reports-api.ts';

const POLL_MS = 3000;
const BYTES_PER_KB = 1024;
const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });

const STATUS: Record<ReportExport['status'], string> = {
  QUEUED: 'Waiting to start',
  RUNNING: 'Generating…',
  DONE: 'Completed',
  FAILED: 'Failed',
};

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportsPanel({
  organisationId,
  documentId,
  documentName,
  range,
  onClose,
}: {
  organisationId: string;
  documentId: string;
  documentName: string;
  range: RangeSetting;
  onClose: () => void;
}): ReactNode {
  const [items, setItems] = useState<ReportExport[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () =>
      exportsApi.list(organisationId, documentId).then(setItems, () => {
        setItems([]);
        setProblem('Downloads could not be loaded.');
      }),
    [organisationId, documentId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const working = (items ?? []).some(
    (item) => item.status === 'QUEUED' || item.status === 'RUNNING',
  );
  useEffect(() => {
    if (!working) return undefined;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [working, load]);

  const make = () => {
    setBusy(true);
    exportsApi
      .request(organisationId, documentId, range)
      .then(load, (error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'The PDF could not be started.'),
      )
      .finally(() => setBusy(false));
  };
  const download = (item: ReportExport) =>
    exportsApi.file(organisationId, item.id).then(
      (blob) => saveBlob(blob, `${documentName}.pdf`),
      () => setProblem('That PDF could not be downloaded. Try again.'),
    );

  return (
    <aside className="rs" aria-label="Downloads">
      <header className="rs-head">
        <strong>Downloads</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="rs-body">
        <p className="rs-explain">
          The PDF is made in the background with live numbers for the period you have chosen. We
          email you when it is ready.
        </p>
        <button type="button" className="btn small" disabled={busy} onClick={make}>
          Make a PDF
        </button>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <h3 className="rs-subhead">PDFs</h3>
        {items?.length === 0 && <p className="rs-explain">No PDFs yet.</p>}
        <ul className="rcm-threads" aria-live="polite">
          {(items ?? []).map((item) => (
            <li key={item.id} className="rcm-thread">
              <strong>{STATUS[item.status]}</strong>
              <small>
                Asked {when.format(new Date(item.created_at))}
                {item.file_size
                  ? ` · ${Math.max(1, Math.round(item.file_size / BYTES_PER_KB))} KB`
                  : ''}
              </small>
              {item.status === 'FAILED' && item.error && <small>{item.error}</small>}
              {item.status === 'DONE' && (
                <button type="button" className="btn small" onClick={() => void download(item)}>
                  Download PDF
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
